import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const SIM_DEFS = '/machinesim/api/sim-defs';

function apiErr(e) {
  const fromBody =
    e.response?.data &&
    typeof e.response.data === 'object' &&
    e.response.data.error != null
      ? String(e.response.data.error)
      : null;
  return fromBody || e.message || 'Request failed';
}

export default function StreamGlobalCoreFields() {
  const [fieldKeys, setFieldKeys] = useState([]);
  const [distinct, setDistinct] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [propagating, setPropagating] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${SIM_DEFS}/global-core-field-keys`);
      setFieldKeys(res.data.fieldKeys || []);
      setDistinct(res.data.distinctFieldKeysInDb || []);
    } catch (e) {
      setError(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (key, checked) => {
    setFieldKeys((prev) => {
      const set = new Set(prev);
      if (checked) set.add(key);
      else set.delete(key);
      return [...set].sort((a, b) => a.localeCompare(b));
    });
  };

  const save = async () => {
    setSaveError(null);
    setSyncMsg(null);
    setSaving(true);
    try {
      await axios.put(`${SIM_DEFS}/global-core-field-keys`, { fieldKeys });
      setSyncMsg('Saved and synced to all machine types.');
      await load();
    } catch (e) {
      setSaveError(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const syncOnly = async () => {
    setSaveError(null);
    setSyncMsg(null);
    setSyncing(true);
    try {
      await axios.post(`${SIM_DEFS}/global-core-field-keys/sync`);
      setSyncMsg('Re-synced core flags from the current global list.');
      await load();
    } catch (e) {
      setSaveError(apiErr(e));
    } finally {
      setSyncing(false);
    }
  };

  const propagateFromLathe = async () => {
    setSaveError(null);
    setSyncMsg(null);
    setPropagating(true);
    try {
      const res = await axios.post(`${SIM_DEFS}/stream-fields/propagate-template`, {
        templateCode: 'CNC_LATHE',
      });
      const keys = (res.data.coreFieldKeys || []).join(', ');
      setSyncMsg(
        `Copied ${res.data.fieldsCopiedPerType} core field${res.data.fieldsCopiedPerType === 1 ? '' : 's'} (${keys}) to ${res.data.machineTypesUpdated} other types; non-core rows on those types were removed.`,
      );
      await load();
    } catch (e) {
      setSaveError(apiErr(e));
    } finally {
      setPropagating(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-muted-foreground">Loading…</div>;
  }

  if (error) {
    return (
      <div className="p-4 md:p-8 max-w-xl">
        <p className="text-destructive">{error}</p>
        <Link to="/stream/machine-types" className="text-primary text-sm mt-4 inline-block">
          ← Machine type definitions
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto pb-16">
      <div className="mb-6">
        <Link to="/stream/machine-types" className="text-sm text-muted-foreground hover:text-foreground">
          ← Machine type definitions
        </Link>
        <h2 className="text-xl font-semibold text-foreground mt-2">Global core field keys</h2>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          These are <span className="font-mono text-foreground">fieldKey</span> strings, not per-type. When you save,
          <span className="font-mono text-primary"> isCore</span> is updated on <strong className="text-foreground">every</strong>{' '}
          stream field row whose key matches. That only affects rows that <strong className="text-foreground">already exist</strong> per
          type.           Non-template types only keep <strong className="text-foreground">global core</strong> stream rows (same keys as the
          template’s core set). Use <strong className="text-foreground">Copy core fields from CNC_LATHE</strong> to match that
          on existing DBs, or re-seed. New fields from the API get core from this list when{' '}
          <span className="font-mono">isCore</span> is omitted.
        </p>
      </div>

      {saveError && <p className="mb-4 text-sm text-cn-warning">{saveError}</p>}
      {syncMsg && <p className="mb-4 text-sm text-cn-success">{syncMsg}</p>}

      <div className="rounded-xl border border-border bg-card overflow-hidden mb-6">
        <div className="px-4 py-3 border-b border-border text-sm text-muted-foreground">
          Known keys in the database (toggle to include in global core)
        </div>
        <ul className="max-h-80 overflow-y-auto divide-y divide-border">
          {distinct.length === 0 ? (
            <li className="px-4 py-6 text-muted-foreground text-sm">No stream fields yet. Seed or add fields to machine types first.</li>
          ) : (
            distinct.map((k) => (
              <li key={k} className="flex items-center gap-3 px-4 py-2 font-mono text-sm">
                <input
                  type="checkbox"
                  className="rounded border-border"
                  checked={fieldKeys.includes(k)}
                  onChange={(e) => toggle(k, e.target.checked)}
                />
                <span className="text-primary">{k}</span>
              </li>
            ))
          )}
        </ul>
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          disabled={saving}
          onClick={save}
          className="px-4 py-2 rounded-lg bg-primary hover:opacity-90 text-primary-foreground disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save & sync to all types'}
        </button>
        <button
          type="button"
          disabled={syncing}
          onClick={syncOnly}
          className="px-4 py-2 rounded-lg border border-border text-foreground hover:bg-accent disabled:opacity-50"
        >
          {syncing ? 'Syncing…' : 'Re-sync only'}
        </button>
        <button
          type="button"
          disabled={propagating}
          onClick={propagateFromLathe}
          className="px-4 py-2 rounded-lg border border-cn-warning/50 text-cn-warning hover:bg-cn-warning/20 disabled:opacity-50"
        >
          {propagating ? 'Copying…' : 'Copy core fields from CNC_LATHE to all types'}
        </button>
      </div>

      <p className="text-xs text-muted-foreground mt-6 max-w-xl">
        Per-type <Link to="/stream/machine-types" className="text-muted-foreground hover:text-foreground">Core fields</Link> pages still
        edit the same <span className="font-mono">isCore</span> column; use this page when you want one list to rule all types.
      </p>
    </div>
  );
}
